/**
 * 本地黑名单工具（纯前端兜底）
 *
 * 背景：苹果审核要求"拉黑作者后，其内容在列表中不可见"。但后端黑名单过滤在
 * 未登录 / 接口缓存 / webview 返回不刷新 等场景下不可靠，导致拉黑后列表仍有该文章。
 * 这里把拉黑记录落到 localStorage，列表渲染时本地过滤，保证用户（含审核员）当场
 * 看到内容消失。
 *
 * 局限：清缓存 / 换设备 / 重装即丢失，不同步到其他设备；仅作审核兜底，
 * 后端 AddToBlackList 接口仍照常调用，两条线并存。
 *
 * 存储结构：{ stamp: <最后更新时间戳>, items: [{ authorID, authorName, articleID }] }
 * stamp 供列表页轮询检测黑名单是否变化，避免无谓的重渲染。
 */

const KEY = 'LOCAL_BLOCK_LIST_v1';

function read() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return { stamp: 0, items: [] };
        const obj = JSON.parse(raw);
        if (!obj || !Array.isArray(obj.items)) return { stamp: 0, items: [] };
        return obj;
    } catch (e) {
        return { stamp: 0, items: [] };
    }
}

function write(data) {
    data.stamp = Date.now();
    try {
        localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) {
        // 忽略写入异常（隐私模式 / 存储已满等）
    }
    return data.stamp;
}

/**
 * 加入黑名单。按 authorID 去重；缺 authorID 时退化为按 articleID 去重。
 * @returns {number} 新的版本时间戳
 */
export function addBlock({ authorID, authorName, articleID }) {
    const data = read();
    const exists = data.items.some(it =>
        (authorID && it.authorID === authorID)
        || (!authorID && articleID && it.articleID === articleID)
    );
    if (!exists) {
        data.items.push({
            authorID: authorID || '',
            authorName: authorName || '',
            articleID: articleID || '',
        });
    }
    return write(data);
}

/**
 * 移除黑名单（解除拉黑时调用）。按 authorID 匹配。
 * @returns {number} 新的版本时间戳
 */
export function removeBlock(authorID) {
    const data = read();
    data.items = data.items.filter(it => !(authorID && it.authorID === authorID));
    return write(data);
}

/** 文章ID是否命中黑名单 */
export function isBlockedArticle(articleID) {
    if (!articleID) return false;
    const data = read();
    return data.items.some(it => it.articleID && String(it.articleID) === String(articleID));
}

/**
 * 作者名是否命中黑名单。
 * 列表项只带作者名（obj.Author），不带作者用户ID，故此处只能按名匹配。
 * 存在同名误伤的极小概率，审核场景可接受。
 */
export function isBlockedAuthor(authorName) {
    if (!authorName) return false;
    const data = read();
    return data.items.some(it => it.authorName && it.authorName === authorName);
}

/** 一条列表项是否应被隐藏（文章ID 或 作者名 任一命中） */
export function isBlocked({ articleID, authorName }) {
    return isBlockedArticle(articleID) || isBlockedAuthor(authorName);
}

/** 当前黑名单版本时间戳，供列表页轮询检测变化 */
export function getStamp() {
    return read().stamp;
}

/** 获取全量黑名单（调试用） */
export function getBlockList() {
    return read().items;
}

/**
 * 按本地黑名单过滤一个列表（getList 拿到数据后调用）。
 * 不同列表的作者/文章字段名不同，通过 idKey / authorKey 指定。
 *   - 社区/资讯列表：{ idKey: 'ID', authorKey: 'Author' }
 *   - 视音列表：     { idKey: 'id', authorKey: 'UserName' }
 *   - 评测列表：     { idKey: 'ID', authorKey: 'PNickName' }
 */
export function filterList(list = [], { idKey = 'ID', authorKey = 'Author' } = {}) {
    return list.filter(obj => !isBlocked({
        articleID: obj[idKey],
        authorName: obj[authorKey]
    }));
}

/**
 * 监听本地黑名单变化（webview 从详情页返回触发的 focus / pageshow /
 * visibilitychange，以及 iOS WKWebView 可能吞事件的兜底定时器），
 * 黑名单更新时调用 onChange。返回卸载函数，请在 componentWillUnmount 调用。
 *
 * 用法：
 *   componentDidMount() { this._unwatchBlock = watchLocalBlock(() => {
 *       this.setState({ data: filterList(this.state.data, { idKey, authorKey }) });
 *   }); }
 *   componentWillUnmount() { this._unwatchBlock && this._unwatchBlock(); }
 */
export function watchLocalBlock(onChange) {
    let last = getStamp();
    const check = () => {
        const cur = getStamp();
        if (cur !== last) {
            last = cur;
            onChange();
        }
    };
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    document.addEventListener('visibilitychange', check);
    const timer = setInterval(check, 1500);
    return () => {
        window.removeEventListener('focus', check);
        window.removeEventListener('pageshow', check);
        document.removeEventListener('visibilitychange', check);
        clearInterval(timer);
    };
}
